import React from "react";
import { Badge } from "../../../../components/badge";
import { Button } from "../../../../components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../../components/tooltip";
import { Image as ImageIcon, QrCode, Trash2 } from "lucide-react";
import type { TFunction } from "i18next";
import { DataTableColumn } from "../../../../components/table";
import type {
  DataTableDateRangeValue,
  DataTableNumberRangeValue,
  DataTableOption,
  DataTableSortDirection,
} from "../../../../components/table";
import PopoverTimeRangeFilterV2Component from "../../../../components/customs/table-header/popover-time-range-filter-v2";
import PopoverEventStatusFilterComponent from "../../../../components/customs/table-header/popover-event-status-filter";
import type { EventFilters, EventListForAdminResponse } from "../../types/event.types";
import type { DemoEventCampaignInfo } from "../../../campaign/api/campaign.api";
import { formatEventDate, formatEventTime, getEventStatus } from "../../utils/event.utils";

type BuildEventListColumnsParams = {
  t: TFunction;
  filters: EventFilters;
  updateFilter: React.Dispatch<React.SetStateAction<EventFilters>>;
  isFilterPopupOpen: (key: string) => boolean;
  handleFilterPopupChange: (key: string, open: boolean) => void;
  categoryOptions: DataTableOption[];
  dateSortDirection?: DataTableSortDirection | null;
  onDateSort?: (nextDirection: DataTableSortDirection | null) => void;
  onRowOpen: (eventItem: EventListForAdminResponse) => void;
  onEdit: (eventItem: EventListForAdminResponse) => void;
  onDelete: (eventItem: EventListForAdminResponse) => void;
  onQRCode?: (eventItem: EventListForAdminResponse) => void;
  demoEventCampaignMap: Record<string, DemoEventCampaignInfo>;
};

const getEventStatusColor = (status: string) => {
  switch (status) {
    case "upcoming":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "ongoing":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "completed":
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
};

const getEventStatusHighlightClass = (isFiltered: boolean) =>
  isFiltered
    ? "border-emerald-500/60 font-semibold shadow-sm ring-1 ring-emerald-500/20 dark:ring-emerald-400/20"
    : "";

const getDemoCampaignInfo = (
  eventItem: EventListForAdminResponse,
  demoEventCampaignMap: Record<string, DemoEventCampaignInfo>,
) => {
  const mappedItem = demoEventCampaignMap[eventItem.id];
  const campaign = eventItem.campaign;

  if (!mappedItem && !campaign) {
    return null;
  }

  return {
    campaignName: campaign?.name || mappedItem?.campaignName || "N/A",
    programName: campaign?.programName || mappedItem?.programName || "N/A",
  };
};

const EventTitleCell = ({
  eventItem,
  demoEventCampaignMap,
}: {
  eventItem: EventListForAdminResponse;
  demoEventCampaignMap: Record<string, DemoEventCampaignInfo>;
}) => {
  const campaignInfo = getDemoCampaignInfo(eventItem, demoEventCampaignMap);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="max-w-[160px]">
          <div className="truncate">{eventItem.title}</div>
          {campaignInfo ? (
            <div className="mt-1 flex flex-col gap-1">
              <span className="inline-flex w-fit rounded-full bg-[#F3FBF6] px-2 py-0.5 text-[10px] font-semibold text-[#004232]">
                {campaignInfo.campaignName}
              </span>
              <span className="truncate text-[10px] text-gray-500 dark:text-gray-400">
                {campaignInfo.programName}
              </span>
            </div>
          ) : null}
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        className="max-w-[280px] rounded-xl border border-[#DDEBE3] bg-white px-4 py-3 text-left shadow-lg"
      >
        <div className="space-y-2">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6A7282]">
              Event name
            </div>
            <div className="mt-1 text-sm font-semibold text-[#0F172A]">{eventItem.title}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6A7282]">
              Program name
            </div>
            <div className="mt-1 text-sm text-[#0F172A]">{campaignInfo?.programName || "N/A"}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6A7282]">
              Campaign name
            </div>
            <div className="mt-1 text-sm text-[#0F172A]">{campaignInfo?.campaignName || "N/A"}</div>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const EventCategoriesCell = ({ eventItem }: { eventItem: EventListForAdminResponse }) => {
  const categories = eventItem.category ? [eventItem.category] : [];
  const categoryNames = [
    ...categories.map((c) => c.name),
    ...(eventItem.interests?.map((i) => i.name) || []),
  ];

  return (
    <div
      className="max-h-[90px] w-[120px] overflow-hidden text-center"
      title={categoryNames.join(", ")}
    >
      <div className="flex flex-col items-center justify-center gap-1">
        {categories.length ? (
          <div className="flex flex-wrap justify-center gap-1">
            {categories.map((category) => (
              <span
                key={category.id}
                className="text-xs font-bold leading-tight text-gray-900 dark:text-gray-400"
              >
                {category.name}
              </span>
            ))}
          </div>
        ) : null}
        {eventItem.interests?.length ? (
          <div className="flex flex-wrap justify-center gap-1">
            {eventItem.interests.map((interest) => (
              <span
                key={interest.id}
                className="text-xs leading-tight text-gray-500 dark:text-gray-200"
              >
                {interest.name}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const EventActionsCell = ({
  eventItem,
  onEdit,
  onDelete,
  onQRCode,
}: {
  eventItem: EventListForAdminResponse;
  onEdit: (eventItem: EventListForAdminResponse) => void;
  onDelete: (eventItem: EventListForAdminResponse) => void;
  onQRCode?: (eventItem: EventListForAdminResponse) => void;
}) => (
  <div className="flex gap-2">
    {onQRCode ? (
      <Button
        size="sm"
        variant="outline"
        className="h-8 w-8 rounded-md border border-gray-300 bg-white p-0 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
        onClick={(e) => {
          e.stopPropagation();
          onQRCode(eventItem);
        }}
      >
        <QrCode className="h-4 w-4 text-gray-700 dark:text-gray-300" />
      </Button>
    ) : null}

    <Button
      size="sm"
      variant="outline"
      className="h-8 w-8 rounded-md border border-gray-300 bg-white p-0 text-red-600 hover:bg-gray-50 hover:text-red-700 dark:border-gray-600 dark:bg-gray-800 dark:text-red-600 dark:hover:text-red-700"
      onClick={(e) => {
        e.stopPropagation();
        onDelete(eventItem);
      }}
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  </div>
);

const updateEventFilter = (
  updateFilter: React.Dispatch<React.SetStateAction<EventFilters>>,
  key: keyof EventFilters,
  value: EventFilters[keyof EventFilters],
) => {
  updateFilter((prev) => ({
    ...prev,
    [key]: value,
  }));
};

const updateStringFilter =
  (updateFilter: React.Dispatch<React.SetStateAction<EventFilters>>, key: keyof EventFilters) =>
  (value: string) =>
    updateEventFilter(updateFilter, key, value as never);

const updateNumberRangeFilter =
  (updateFilter: React.Dispatch<React.SetStateAction<EventFilters>>, key: keyof EventFilters) =>
  (value: DataTableNumberRangeValue) =>
    updateEventFilter(updateFilter, key, value as never);

const updateDateRangeFilter =
  (updateFilter: React.Dispatch<React.SetStateAction<EventFilters>>, key: keyof EventFilters) =>
  (value: DataTableDateRangeValue) =>
    updateEventFilter(updateFilter, key, value as never);

const makeFilterState = (
  filterKey: string,
  filters: EventFilters,
  updateFilter: React.Dispatch<React.SetStateAction<EventFilters>>,
) =>
  ({
    filterKey,
    filter: filters,
    setFilter: updateFilter,
  }) as any;

export const buildEventListColumns = ({
  t,
  filters,
  updateFilter,
  isFilterPopupOpen,
  handleFilterPopupChange,
  categoryOptions,
  dateSortDirection,
  onDateSort,
  onEdit,
  onDelete,
  onQRCode,
  demoEventCampaignMap,
}: BuildEventListColumnsParams): DataTableColumn<EventListForAdminResponse>[] => [
  {
    key: "thumbnail",
    title: t("event.thumbnail"),
    render: (eventItem) =>
      eventItem.image_url ? (
        <img
          src={eventItem.image_url}
          alt={eventItem.title}
          className="h-12 w-12 rounded-lg object-cover"
        />
      ) : (
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
          <ImageIcon className="h-6 w-6 text-gray-400" />
        </div>
      ),
    width: 80,
    cellClassName: "w-16",
  },
  {
    key: "title",
    title: t("event.name"),
    filter: {
      type: "string",
      value: filters.title,
      onFilter: updateStringFilter(updateFilter, "title"),
      placeholder: `Search ${t("event.name").toLowerCase()}...`,
      title: `FILTER BY ${t("event.name").toUpperCase()}`,
      description: "Press Enter to search",
      submitMode: "enter",
    },
    render: (eventItem) => (
      <EventTitleCell
        eventItem={eventItem}
        demoEventCampaignMap={demoEventCampaignMap}
      />
    ),
    cellClassName: "w-32",
  },
  {
    key: "category",
    title: t("event.category"),
    filter: {
      type: "dropdown",
      value: filters.category_id,
      onFilter: (value: string) =>
        updateFilter((prev) => ({
          ...prev,
          category_id: value,
        })),
      options: categoryOptions,
      title: "FILTER BY CATEGORY",
      placeholder: "Select category...",
      applyLabel: "Apply",
      clearLabel: "Clear",
    },
    render: (eventItem) => <EventCategoriesCell eventItem={eventItem} />,
    cellClassName: "w-40",
    align: "center",
  },
  {
    key: "event_date",
    title: t("event.date"),
    filter: {
      type: "range_date",
      value: filters.event_date,
      onFilter: updateDateRangeFilter(updateFilter, "event_date"),
      title: "FILTER BY DATE RANGE",
    },
    sort: onDateSort
      ? {
          direction: dateSortDirection ?? null,
          onSort: onDateSort,
          title: t("event.date"),
        }
      : undefined,
    render: (eventItem) => (
      <span className="text-xs">
        {formatEventDate(eventItem.event_date || "", eventItem.time_zone)}
      </span>
    ),
  },
  {
    key: "location",
    title: t("event.location"),
    filter: {
      type: "string",
      value: filters.location,
      onFilter: updateStringFilter(updateFilter, "location"),
      placeholder: `Search ${t("event.location").toLowerCase()}...`,
      title: `FILTER BY ${t("event.location").toUpperCase()}`,
      description: "Press Enter to search",
      submitMode: "enter",
    },
    render: (eventItem) => (
      <div
        className="max-w-[150px] truncate text-xs"
        title={eventItem.location || `${eventItem.latitude}, ${eventItem.longitude}`}
      >
        {eventItem.location || `${eventItem.latitude}, ${eventItem.longitude}`}
      </div>
    ),
    cellClassName: "w-32",
  },
  {
    key: "event_time",
    title: t("common.time"),
    filter: (
      <PopoverTimeRangeFilterV2Component
        filterState={makeFilterState("event_time", filters, updateFilter)}
        title="FILTER BY TIME RANGE"
        isOpen={isFilterPopupOpen("event_time")}
        onOpenChange={(open) => handleFilterPopupChange("event_time", open)}
      />
    ),
    render: (eventItem) => {
      const startTime = formatEventTime(eventItem.start_time || "", eventItem.time_zone);
      const endTime = formatEventTime(eventItem.end_time || "", eventItem.time_zone);

      return (
        <span className="whitespace-nowrap text-xs">
          {`${startTime}-${endTime}`} <br /> {eventItem.time_zone}
        </span>
      );
    },
    cellClassName: "w-28",
    align: "center",
  },
  {
    key: "points",
    title: t("event.points"),
    filter: {
      type: "range_number",
      value: filters.points,
      onFilter: updateNumberRangeFilter(updateFilter, "points"),
      title: "FILTER BY POINTS RANGE",
      minLabel: "Min Points",
      maxLabel: "Max Points",
      placeholder: "points",
    },
    render: (eventItem) => <span className="text-xs font-medium">{eventItem.points || 0}</span>,
    align: "center",
  },
  {
    key: "attending",
    title: t("event.attending"),
    filter: {
      type: "range_number",
      value: filters.attending,
      onFilter: updateNumberRangeFilter(updateFilter, "attending"),
      title: "FILTER BY ATTENDING RANGE",
      minLabel: "Min Attending",
      maxLabel: "Max Attending",
      placeholder: "attendees",
    },
    render: (eventItem) => (
      <span className="text-xs font-medium">{eventItem.attending_count || 0}</span>
    ),
    align: "center",
  },
  {
    key: "checkin",
    title: t("event.checkin"),
    filter: {
      type: "range_number",
      value: filters.checkin,
      onFilter: updateNumberRangeFilter(updateFilter, "checkin"),
      title: "FILTER BY CHECKIN RANGE",
      minLabel: "Min Checked in",
      maxLabel: "Max Checked in",
      placeholder: "Checked in",
    },
    render: (eventItem) => (
      <span className="text-xs font-medium">{eventItem.checkin_count || 0}</span>
    ),
    align: "center",
  },
  {
    key: "status",
    title: t("common.status"),
    filter: (
      <PopoverEventStatusFilterComponent
        filterState={makeFilterState("status", filters, updateFilter)}
        title="FILTER BY STATUS"
        isOpen={isFilterPopupOpen("status")}
        onOpenChange={(open) => handleFilterPopupChange("status", open)}
      />
    ),
    render: (eventItem) => {
      const status = getEventStatus(eventItem);
      const label =
        status === "upcoming"
          ? t("event.upcoming")
          : status === "ongoing"
            ? t("event.ongoing")
            : t("event.completed");
      const isStatusFiltered = filters.status.length > 0 && filters.status.includes(status);

      return (
        <Badge
          className={`${getEventStatusColor(status)} ${getEventStatusHighlightClass(isStatusFiltered)}`}
        >
          {label}
        </Badge>
      );
    },
    align: "center",
  },
  {
    key: "actions",
    title: t("common.actions"),
    render: (eventItem) => (
      <EventActionsCell
        eventItem={eventItem}
        onEdit={onEdit}
        onDelete={onDelete}
        onQRCode={onQRCode}
      />
    ),
    align: "center",
    cellClassName: "w-32",
  },
];
